import React, { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useRef } from 'react';
import Quill from 'quill';

/**
 * First-party React wrapper around Quill 2.x (Quill's official React example pattern).
 * Replaces the react-quill-new dependency while preserving the slice of its API the app
 * relies on: a `getEditor()` ref handle and a 4-arg `onChange(content, delta, source, editor)`
 * where `content` is the editor HTML.
 */
const QuillEditor = forwardRef((props, ref) => {
    const {
        theme = 'snow',
        value,
        modules,
        formats,
        placeholder,
        onChange,
        onKeyDown,
        className,
    } = props;

    const containerRef = useRef(null);
    const quillRef = useRef(null);
    const onChangeRef = useRef(onChange);

    useLayoutEffect(() => {
        onChangeRef.current = onChange;
    });

    // Drop-in compatibility: consumer accesses the underlying instance via ref.current.getEditor()
    useImperativeHandle(ref, () => ({ getEditor: () => quillRef.current }), []);

    useEffect(() => {
        const container = containerRef.current;
        const editorEl = container.appendChild(document.createElement('div'));
        const quill = new Quill(editorEl, { theme, modules, formats, placeholder });
        quillRef.current = quill;

        if (value) {
            quill.clipboard.dangerouslyPasteHTML(value);
        }

        quill.on('text-change', (delta, _oldDelta, source) => {
            onChangeRef.current && onChangeRef.current(quill.root.innerHTML, delta, source, quill);
        });

        return () => {
            quillRef.current = null;
            container.innerHTML = '';
        };
        // Instantiate Quill once; content/selection are driven imperatively via getEditor().
    }, []);

    // `quill` class mirrors react-quill-new's root wrapper so existing CSS
    // (e.g. #quill-container .quill .ql-container { height }) keeps applying.
    return (
        <div
            ref={containerRef}
            className={className ? `quill ${className}` : 'quill'}
            onKeyDown={onKeyDown}
        />
    );
});

QuillEditor.displayName = 'QuillEditor';

export default QuillEditor;
